import { FunctionComponent, h } from 'preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { GoogleMap, Polyline, useLoadScript } from '@react-google-maps/api';
import classNames from 'classnames';
import { getEnv } from 'mobx-easy';
import { isEmpty as _isEmpty, isFunction as _isFunction } from 'lodash';
import { Location } from '@bringg/types';
import { RootEnv } from '../../helpers/create-store';
import { useTheme } from '../../hooks/use-theme/use-theme';
import { GoogleTravelMode } from '../../types/shared-location.types';
import MapSettings from '../map-settings/map-settings';
import MapAutoCenter from '../map-auto-center/map-auto-center';
import MapZoom from '../map-zoom/map-zoom';
import MapStreetView from '../map-street-view/map-street-view';
import Translate from '../translate/translate';
import MapMarker, { MarkerData } from './map-marker/map-marker';
import { useTranslation } from '../../hooks/use-translation';

interface Props {
	markers: MarkerData[];
	routePath: Location[];
	travelMode: GoogleTravelMode;
	onMapLoad?: () => {};
	mapHeight?: string;
	maxZoom?: number;
	enableStreetView?: boolean;
	enablePolyline?: boolean;
	topActionsClass?: string;
	center?: google.maps.LatLngLiteral;
}

const DEFAULT_CENTER: google.maps.LatLngLiteral = {
	lat: 40.7232048,
	lng: -73.8791339
};

const DEFAULT_ZOOM = 10;
const BOUNDS_MIN_ZOOM = 15;
const BOUNDS_PADDING = 40;
const DEFAULT_POLYLINE_OPACITY = 0.8;
const DEFAULT_POLYLINE_WIDTH = 5;

const validatePosition = position => position && position.lat && position.lng;

const validateMarkerPosition = ({ position }: MarkerData) => validatePosition(position);

const Map: FunctionComponent<Props> = ({
	markers,
	routePath = [],
	onMapLoad,
	travelMode,
	mapHeight,
	maxZoom,
	enablePolyline = true,
	enableStreetView,
	topActionsClass,
	center
}) => {
	const [map, setMap] = useState<google.maps.Map>(null);
	const [bounds, setBounds] = useState(null);
	const [route, setRoute] = useState<google.maps.LatLng[]>([]);
	const { primaryColor } = useTheme();
	const { t } = useTranslation();

	useEffect(() => {
		if (map && !center) {
			const updatedBounds = new google.maps.LatLngBounds();
			markers.forEach(({ position }) => validatePosition(position) && updatedBounds.extend(position));
			setBounds(updatedBounds);
		}
	}, [map, markers]);

	useEffect(() => {
		if (map && bounds) {
			map.fitBounds(bounds, BOUNDS_PADDING);
			applyMinimumZoom();
		}
	}, [map, bounds]);

	const mapMarkers = useMemo(
		() =>
			markers
				.filter(validateMarkerPosition)
				.map(({ position, icon }, index) => <MapMarker position={position} icon={icon} key={index} />),
		[markers]
	);

	const handleMapLoad = useCallback(map => {
		setMap(map);
		if (_isFunction(onMapLoad)) {
			onMapLoad();
		}
	}, []);

	const isPathValid = useMemo(() => routePath.every(({ lat, lng }) => Boolean(lat && lng)), [routePath]);

	const getDirectionsForRoute = useCallback(async () => {
		if (!isPathValid) {
			return;
		}

		try {
			const [origin, destination] = routePath;
			const { directionService } = getEnv<RootEnv>();
			const route = await directionService.getRoute(origin, destination, travelMode);
			setRoute(route);
		} catch (e) {
			console.error('Failed to get directions to route', e);
		}
	}, [isPathValid, routePath, travelMode]);

	useEffect(() => {
		if (map && !_isEmpty(routePath) && enablePolyline) {
			getDirectionsForRoute();
		}
	}, [map, getDirectionsForRoute]);

	const polylineOptions = useMemo(
		() => ({
			strokeColor: primaryColor,
			strokeOpacity: DEFAULT_POLYLINE_OPACITY,
			strokeWeight: DEFAULT_POLYLINE_WIDTH
		}),
		[primaryColor]
	);

	const applyMinimumZoom = () => {
		const listener = google.maps.event.addListener(map, 'bounds_changed', () => {
			map.setZoom(Math.min(BOUNDS_MIN_ZOOM, map.getZoom()));
			google.maps.event.removeListener(listener);
		});
	};

	const [position] = routePath;
	const displayStreetViewOption = enableStreetView && Boolean(position && position.lat && position.lng);
	return (
		<div className="map-container" aria-label={t('TRACKING.MAP_CONTAINER_ARIA_LABEL')} tab-index={0}>
			<div className="map-container" aria-hidden={true} tabIndex={-1}>
				<GoogleMap
					mapContainerStyle={{ height: mapHeight }}
					id="customer-app-map"
					zoom={DEFAULT_ZOOM}
					center={center || DEFAULT_CENTER}
					mapContainerClassName="map-container"
					onLoad={handleMapLoad}
					options={{ disableDefaultUI: true, maxZoom }}
				>
					<div className={classNames('map-top-actions', topActionsClass)}>
						<MapSettings map={map} />
						{displayStreetViewOption && <MapStreetView position={position} />}
					</div>
					{mapMarkers}
					{isPathValid && <Polyline path={route} options={polylineOptions} />}
					<MapZoom map={map} />
					<MapAutoCenter map={map} bounds={bounds} />
				</GoogleMap>
			</div>
		</div>
	);
};

export default ({ ...otherProps }: Props) => {
	const googleMapsKey = getEnv<RootEnv>().googleMapsApiKey;

	const { isLoaded, loadError } = useLoadScript({
		googleMapsApiKey: googleMapsKey,
		preventGoogleFontsLoading: true
	});

	return (
		<div className="map-loader">
			{loadError && (
				<div className="google-map-loading-error">
					<Translate text="MAP_LOADING_FAILED" />
				</div>
			)}
			{isLoaded && <Map {...otherProps} />}
		</div>
	);
};
